import { useEffect, useRef, useState } from 'react';
import { Copy, Check, X } from 'lucide-react';
import './ErrorToast.css';

/** Shape of the API's JSON error envelope (www/api/errors). `detail` is only
 *  present when the operator enables DETAILED_ERRORS on the server. */
export interface ApiErrorPayload {
    error?: string;
    hint?: string;
    detail?: string;
}

export interface ToastError {
    /** Short headline, e.g. "Failed to apply chosen cover art". */
    title: string;
    /** Actionable remediation from the server ("Check filesystem permissions…"). */
    hint?: string;
    /** Underlying cause, server-sanitised. Shown in a copyable block. */
    detail?: string;
    /** Steps that were skipped by a partial apply, if any. */
    skippedSteps?: string[];
}

/** Builds a ToastError from a failed fetch Response, falling back gracefully
 *  when the body is not the expected JSON envelope (a proxy 502 returning
 *  HTML, for instance) - the status line is still more useful to someone
 *  self-hosting than a bare "something went wrong". */
export async function toastErrorFromResponse(response: Response, title: string): Promise<ToastError> {
    try {
        const payload: ApiErrorPayload = await response.json();
        return {
            title,
            hint: payload.hint,
            detail: payload.detail ?? payload.error ?? `HTTP ${response.status} ${response.statusText}`,
        };
    } catch {
        return { title, detail: `HTTP ${response.status} ${response.statusText}` };
    }
}

/** Builds a ToastError from a thrown exception (network down, CORS, abort). */
export function toastErrorFromException(err: unknown, title: string): ToastError {
    return {
        title,
        hint: 'The request never reached the server. Check that the server is running and reachable.',
        detail: err instanceof Error ? `${err.name}: ${err.message}` : String(err),
    };
}

interface Props {
    error: ToastError | null;
    onDismiss: () => void;
}

/** A dismissible error popup that reports the full, copyable cause.
 *
 *  Deliberately NOT auto-dismissing and NOT tied to the lifetime of whatever
 *  produced it: a failed apply used to clear the candidate list, throwing away
 *  the identification results and forcing the user to search again. The toast
 *  renders alongside those results instead, so they survive the error.
 *
 *  The detail block is selectable and has a copy button, because the intended
 *  audience is someone self-hosting this who needs to paste the real error into
 *  a shell, an issue, or a search box. */
export function ErrorToast({ error, onDismiss }: Props): React.JSX.Element | null {
    const [copied, setCopied] = useState(false);
    const copyResetRef = useRef<number | undefined>(undefined);

    // Reset the copy affordance whenever a different error is shown, so a new
    // failure never appears pre-confirmed as copied.
    useEffect(() => {
        setCopied(false);
    }, [error]);

    useEffect(() => {
        return () => {
            if (copyResetRef.current !== undefined) window.clearTimeout(copyResetRef.current);
        };
    }, []);

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onDismiss();
        };
        if (error) document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [error, onDismiss]);

    if (!error) return null;

    const copyText = [
        error.title,
        error.skippedSteps?.length ? `Skipped: ${error.skippedSteps.join(', ')}` : undefined,
        error.hint,
        error.detail,
    ]
        .filter(Boolean)
        .join('\n');

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(copyText);
            setCopied(true);
            copyResetRef.current = window.setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard API is unavailable outside a secure context (plain
            // HTTP on a LAN address is a normal self-hosting setup). The text
            // is selectable, so manual copy still works - just leave the
            // button un-confirmed rather than claiming success.
        }
    };

    return (
        <div className="errorToast" role="alert" aria-live="assertive">
            <div className="errorToastHeader">
                <span className="errorToastTitle">{error.title}</span>
                <button type="button" className="errorToastClose" onClick={onDismiss} aria-label="Dismiss error">
                    <X size={14} />
                </button>
            </div>

            {error.skippedSteps && error.skippedSteps.length > 0 && (
                <div className="errorToastSteps">Skipped: {error.skippedSteps.join(', ')}</div>
            )}

            {error.hint && <div className="errorToastHint">{error.hint}</div>}

            {error.detail && (
                <div className="errorToastDetailWrap">
                    <pre className="errorToastDetail">{error.detail}</pre>
                    <button type="button" className="errorToastCopy" onClick={copy}>
                        {copied ? <Check size={12} /> : <Copy size={12} />}
                        {copied ? 'Copied' : 'Copy'}
                    </button>
                </div>
            )}
        </div>
    );
}
